/**
 * Derives the primary CTA for the producer overview/dashboard.
 *
 * Reuses the canonical offering → section mapping from nav.producer.ts
 * to determine whether activity creation is contextually relevant.
 *
 * This is a **soft gate**: activities can always be created,
 * but copy adapts to the producer's offering profile.
 */
import { getProducerNavVisibility } from '@/config/nav.producer';

export interface OverviewCtaInput {
  /** Active offering keys from producer_offerings */
  offeringKeys: string[];
  /** Number of public events */
  publicEventCount: number;
  /** First draft event, if any */
  firstDraft: { id: string; title: string } | null;
  /** Route prefix, e.g. /app/producer/farm/:farmId */
  basePath: string;
}

export interface OverviewCtaResult {
  title: string;
  subtitle: string;
  ctaType: 'create_first' | 'finish_draft' | 'create_new' | 'complement_activity';
  href: string;
}

/**
 * Returns the primary CTA config for the producer dashboard.
 *
 * Logic:
 * 1. If drafts exist → finish draft (always highest priority)
 * 2. If offerings signal activities AND no public events → "Skapa din första aktivitet"
 * 3. If offerings do NOT signal activities AND no events → soft CTA to complement with activity
 * 4. If public events exist → "Skapa ny aktivitet"
 */
export function getOverviewPrimaryCta(input: OverviewCtaInput): OverviewCtaResult {
  const { offeringKeys, publicEventCount, firstDraft, basePath } = input;
  const vis = getProducerNavVisibility(offeringKeys);

  // 1. Drafts always take priority
  if (firstDraft) {
    return {
      title: `Färdigställ "${firstDraft.title}"`,
      subtitle: 'Gör den synlig för besökare',
      ctaType: 'finish_draft',
      href: `${basePath}/events/${firstDraft.id}/edit`,
    };
  }

  // 2. No events at all
  if (publicEventCount === 0) {
    // Activity-signalling offerings → direct CTA
    if (vis.showActivities) {
      return {
        title: 'Skapa din första aktivitet',
        subtitle: 'Börja ta emot bokningar',
        ctaType: 'create_first',
        href: `${basePath}/events/new`,
      };
    }

    // Non-activity offerings (e.g. Boende only) → soft complement CTA
    return {
      title: 'Komplettera med en aktivitet',
      subtitle: 'Aktiviteter kan locka fler besökare till er gård',
      ctaType: 'complement_activity',
      href: `${basePath}/events/new`,
    };
  }

  // 3. Has public events → create more
  return {
    title: 'Skapa ny aktivitet',
    subtitle: 'Lägg till fler datum eller något nytt',
    ctaType: 'create_new',
    href: `${basePath}/events/new`,
  };
}
